/**
 * Kundenstamm-Anlage aus einer Verordnung.
 *
 * Der Scan gleicht bereits serverseitig gegen den Kundenstamm ab
 * (`enrichPrescriptionWithCustomerMaster`) und liefert bei einem eindeutigen
 * Treffer die Kundennummer mit. Ohne Treffer endete das bisher stillschweigend —
 * der Patient blieb ein Name auf der Verordnung, ohne Kundenakte. Diese Datei
 * liefert die Bausteine, um ihn anzulegen, ohne dabei Dubletten zu erzeugen.
 */

/**
 * Der Scanner liefert den Namen als „Nachname, Vorname" — so kann der
 * DMRZ-Export ihn später sicher in zwei Spalten trennen. Der Kundenstamm führt
 * ihn dagegen in Leserichtung.
 */
export const patientNameToCustomerName = (patientName = '') => {
  const value = String(patientName).replace(/\s+/g, ' ').trim();
  if (!value.includes(',')) return value;
  const [last, ...rest] = value.split(',');
  const first = rest.join(' ').trim();
  return first ? `${first} ${last.trim()}` : last.trim();
};

/** Vergleichsform: ohne Satzzeichen, ohne Reihenfolge, kleingeschrieben. */
const nameTokens = (value = '') => String(value)
  .toLowerCase()
  .replace(/[^a-zäöüß\s]/g, ' ')
  .split(/\s+/)
  .filter(part => part.length > 1)
  .sort();

/**
 * Namensvergleich unabhängig von Reihenfolge und Satzzeichen — „Stry, Erika"
 * aus dem Scan und „Erika Stry" aus dem Stamm sind dieselbe Person.
 */
export const sameName = (a, b) => {
  const left = nameTokens(a);
  const right = nameTokens(b);
  if (left.length === 0 || right.length === 0) return false;
  return left.join(' ') === right.join(' ');
};

/** Datum als `JJJJ-MM-TT`, egal ob Timestamp, Date oder String. */
export const toDateKey = (value) => {
  if (!value) return '';
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : (value?.seconds ? new Date(value.seconds * 1000) : new Date(value));
  return Number.isNaN(date?.getTime?.()) ? '' : date.toISOString().slice(0, 10);
};

const customerFields = (customer) => ({
  name: customer?.name || customer?.user?.name || '',
  birthDate: customer?.birthDate || customer?.user?.birthDate || null
});

/**
 * Gibt es im Kundenstamm schon jemanden, der dieser Verordnung entspricht?
 *
 * Bewusst zweistufig: Ein Treffer bei Name **und** Geburtsdatum ist sicher,
 * ein Treffer nur beim Namen ist ein Verdacht — Namensgleichheit kommt vor,
 * und die Verordnung liefert das Geburtsdatum ohnehin fast immer mit.
 */
export const findCustomerCandidates = (customers, { patientName, patientBirthDate }) => {
  const wanted = patientNameToCustomerName(patientName);
  if (!wanted) return { exact: null, similar: [] };

  const wantedBirthKey = toDateKey(patientBirthDate);
  const similar = [];
  let exact = null;

  for (const customer of customers || []) {
    const fields = customerFields(customer);
    if (!sameName(fields.name, wanted)) continue;

    const birthKey = toDateKey(fields.birthDate);
    if (wantedBirthKey && birthKey && wantedBirthKey === birthKey) {
      exact = exact || customer;
    } else {
      similar.push(customer);
    }
  }

  return { exact, similar };
};

/** Anschrift des Versicherten in einer Zeile, wie der Kundenstamm sie führt. */
export const buildCustomerAddress = (form) => {
  const street = [form.patientStreet, form.patientHouseNumber]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  const city = [form.patientZip, form.patientCity]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  return [street, city].filter(Boolean).join(', ');
};

/**
 * Vorbelegung für die Kundenanlage. Der Personenkreis-Snapshot der Verordnung
 * wandert mit in den Kundenstamm — dort gehört er hin, damit die nächste
 * Verordnung ihn wiederfindet, statt ihn erneut zu erfragen.
 */
/**
 * Telefonkunden führt die Zentrale selbst, App-Kunden pflegen ihre Daten im
 * eigenen Konto. Deshalb darf nur bei Telefonkunden aus einer Verordnung
 * heraus überschrieben werden.
 */
export const isPhoneCustomer = (customer) => (
  customer?.isPhoneCustomer === true || customer?.user?.isPhoneCustomer === true
);

const hasEligibilityContent = (eligibility) => Boolean(
  eligibility && (
    (Array.isArray(eligibility.mobilityMarks) && eligibility.mobilityMarks.length > 0)
    || String(eligibility.careLevel || '').trim()
    || eligibility.permanentMobilityImpairment === true
    || String(eligibility.notes || '').trim()
  )
);

/**
 * Was aus der Verordnung in die bestehende Kundenakte übernommen wird.
 *
 * Enthält **nur befüllte** Angaben: Muster 4 führt zum Beispiel keine
 * Rufnummer, und ein leeres Feld aus der Verordnung darf eine gepflegte
 * Kundennummer nicht löschen. `updateCustomer` schreibt Geburtsdatum und
 * Anschrift bereits bei `!== undefined`, ein leerer Wert käme dort also
 * tatsächlich an.
 */
export const buildCustomerUpdate = (form) => {
  const update = {};

  const name = patientNameToCustomerName(form.patientName);
  if (name) update.name = name;

  if (form.patientBirthDate) update.birthDate = form.patientBirthDate;

  const address = buildCustomerAddress(form);
  if (address) update.address = address;

  const phone = String(form.patientPhone || '').trim();
  if (phone) update.phoneNumber = phone;

  const eligibility = form.medicalTransportEligibilitySnapshot;
  if (hasEligibilityContent(eligibility)) {
    update.medicalTransportEligibility = {
      mobilityMarks: eligibility.mobilityMarks || [],
      careLevel: eligibility.careLevel || '',
      permanentMobilityImpairment: eligibility.permanentMobilityImpairment === true,
      notes: eligibility.notes || ''
    };
  }

  return update;
};

export const buildCustomerDraft = (form) => ({
  name: patientNameToCustomerName(form.patientName),
  phoneNumber: String(form.patientPhone || '').trim(),
  birthDate: form.patientBirthDate || '',
  address: buildCustomerAddress(form) || String(form.pickupAddress || '').trim(),
  medicalTransportEligibility: {
    mobilityMarks: form.medicalTransportEligibilitySnapshot?.mobilityMarks || [],
    careLevel: form.medicalTransportEligibilitySnapshot?.careLevel || '',
    permanentMobilityImpairment:
      form.medicalTransportEligibilitySnapshot?.permanentMobilityImpairment === true,
    notes: form.medicalTransportEligibilitySnapshot?.notes || ''
  }
});
